import { type CSSProperties, useEffect, useRef, useState } from "react";
import {
	type AnimalEntry,
	fallbackAnimals,
	fetchAnimals,
	findAnimalBySlug,
	formatAnimalLabel,
	formatAnimalName,
	getRandomAnimal,
} from "./animal-data";
import { CreatorBanner } from "./components/CreatorBanner";
import { EndpointsBlock } from "./components/EndpointsBlock";
import { SiteChrome } from "./components/SiteChrome";

const copyConfirmationDuration = 1800;
const fallbackSpotlightAnimal =
	findAnimalBySlug(fallbackAnimals, "hedgehog") ?? fallbackAnimals[0] ?? null;

const heroFloaters = [
	{ animal: "fox", className: "floater-fox bob-a" },
	{ animal: "owl", className: "floater-owl bob-b" },
	{ animal: "hedgehog", className: "floater-hedgehog bob-c" },
	{ animal: "capybara", className: "floater-capybara bob-d" },
	{ animal: "koala", className: "floater-koala bob-a" },
];

const renderRosterItems = (
	keyPrefix: string,
	animals: AnimalEntry[],
	onSelect: (animal: AnimalEntry) => void,
) =>
	animals.map((animal) => (
		<button
			key={`${keyPrefix}-${animal.animal}`}
			type="button"
			className="roster-animal"
			onClick={() => onSelect(animal)}
			aria-label={`Feature the ${formatAnimalLabel(animal.animal)}`}
		>
			<img src={animal.imageUrl} alt="" decoding="async" />
		</button>
	));

/**
 * Keep the roster loop seamless by shifting exactly one measured group width.
 */
const RosterStrip = ({
	animals,
	onSelectAnimal,
}: {
	animals: AnimalEntry[];
	onSelectAnimal: (animal: AnimalEntry) => void;
}) => {
	const stripRef = useRef<HTMLDivElement>(null);
	const trackRef = useRef<HTMLDivElement>(null);
	const pointerRef = useRef({ x: 0, y: 0, active: false });
	const hoveredButtonRef = useRef<HTMLButtonElement | null>(null);

	useEffect(() => {
		const setHoveredButton = (button: HTMLButtonElement | null) => {
			if (hoveredButtonRef.current === button) {
				return;
			}

			hoveredButtonRef.current?.classList.remove("is-hovered");
			button?.classList.add("is-hovered");
			hoveredButtonRef.current = button;
		};

		const pickButtonAtPointer = () => {
			if (!pointerRef.current.active) {
				setHoveredButton(null);
				return;
			}

			const hit = document.elementFromPoint(
				pointerRef.current.x,
				pointerRef.current.y,
			);
			const button = hit?.closest(".roster-animal") as HTMLButtonElement | null;

			setHoveredButton(button ?? null);
		};

		const track = trackRef.current;
		if (!track) {
			return;
		}

		const syncLoopWidth = () => {
			const group = track.querySelector<HTMLElement>(".roster-group");
			if (!group) {
				return;
			}

			track.style.setProperty("--roster-shift", `${group.offsetWidth}px`);
		};

		syncLoopWidth();

		const resizeObserver = new ResizeObserver(syncLoopWidth);
		const firstGroup = track.querySelector(".roster-group");
		if (firstGroup) {
			resizeObserver.observe(firstGroup);
		}

		for (const image of Array.from(track.querySelectorAll("img"))) {
			if (!image.complete) {
				image.addEventListener("load", syncLoopWidth, { once: true });
			}
		}

		let frameId: number | null = null;
		const tick = () => {
			pickButtonAtPointer();
			frameId = requestAnimationFrame(tick);
		};

		const strip = stripRef.current;
		if (!strip) {
			return () => {
				resizeObserver.disconnect();
			};
		}

		const handlePointerEnter = (event: PointerEvent) => {
			pointerRef.current = {
				x: event.clientX,
				y: event.clientY,
				active: true,
			};
			if (frameId === null) {
				frameId = requestAnimationFrame(tick);
			}
		};

		const handlePointerMove = (event: PointerEvent) => {
			pointerRef.current = {
				x: event.clientX,
				y: event.clientY,
				active: true,
			};
		};

		const handlePointerLeave = () => {
			pointerRef.current.active = false;
			if (frameId !== null) {
				cancelAnimationFrame(frameId);
				frameId = null;
			}
			setHoveredButton(null);
		};

		strip.addEventListener("pointerenter", handlePointerEnter);
		strip.addEventListener("pointermove", handlePointerMove);
		strip.addEventListener("pointerleave", handlePointerLeave);

		return () => {
			resizeObserver.disconnect();
			strip.removeEventListener("pointerenter", handlePointerEnter);
			strip.removeEventListener("pointermove", handlePointerMove);
			strip.removeEventListener("pointerleave", handlePointerLeave);
			if (frameId !== null) {
				cancelAnimationFrame(frameId);
			}
			hoveredButtonRef.current?.classList.remove("is-hovered");
		};
	}, []);

	return (
		<div className="roster-strip" ref={stripRef}>
			<div className="roster-track" ref={trackRef}>
				<div className="roster-group">
					{renderRosterItems("a", animals, onSelectAnimal)}
				</div>
				<div className="roster-group" aria-hidden="true">
					{renderRosterItems("b", animals, onSelectAnimal)}
				</div>
			</div>
		</div>
	);
};

const spotlightTargetFill = 0.72;
const spotlightMaxScale = 2.35;

/**
 * Measure how much transparent padding an avatar has so spotlight sizing stays even.
 */
const measureSpotlightScale = async (animalSlug: string) => {
	try {
		const response = await fetch(
			`/api/animals/${encodeURIComponent(animalSlug)}.webp`,
		);
		if (!response.ok) {
			return 1;
		}

		const blob = await response.blob();
		const objectUrl = URL.createObjectURL(blob);

		return await new Promise<number>((resolve) => {
			const image = new Image();

			const finish = (scale: number) => {
				URL.revokeObjectURL(objectUrl);
				resolve(scale);
			};

			image.onload = () => {
				const sampleSize = 256;
				const canvas = document.createElement("canvas");
				canvas.width = sampleSize;
				canvas.height = sampleSize;
				const context = canvas.getContext("2d");

				if (!context) {
					finish(1);
					return;
				}

				context.drawImage(image, 0, 0, sampleSize, sampleSize);
				const { data, width, height } = context.getImageData(
					0,
					0,
					sampleSize,
					sampleSize,
				);

				let minX = width;
				let minY = height;
				let maxX = 0;
				let maxY = 0;

				for (let y = 0; y < height; y += 1) {
					for (let x = 0; x < width; x += 1) {
						const alpha = data[(y * width + x) * 4 + 3] ?? 0;
						if (alpha > 24) {
							minX = Math.min(minX, x);
							minY = Math.min(minY, y);
							maxX = Math.max(maxX, x);
							maxY = Math.max(maxY, y);
						}
					}
				}

				if (maxX <= minX || maxY <= minY) {
					finish(1);
					return;
				}

				const contentSize = Math.max(maxX - minX, maxY - minY);
				const fillRatio = contentSize / sampleSize;
				const scale = spotlightTargetFill / fillRatio;

				finish(Math.min(spotlightMaxScale, Math.max(1, scale)));
			};

			image.onerror = () => {
				finish(1);
			};

			image.src = objectUrl;
		});
	} catch {
		return 1;
	}
};

/**
 * Render the featured animal with per-image scale correction.
 */
const SpotlightAnimalImage = ({
	animal,
	alt,
}: {
	animal: AnimalEntry;
	alt: string;
}) => {
	const [scale, setScale] = useState(1);

	useEffect(() => {
		let isMounted = true;
		setScale(1);

		measureSpotlightScale(animal.animal).then((nextScale) => {
			if (isMounted) {
				setScale(nextScale);
			}
		});

		return () => {
			isMounted = false;
		};
	}, [animal.animal]);

	return (
		<div className="spot-img-bob">
			<img
				src={animal.imageUrl}
				alt={alt}
				style={{ "--spot-scale": scale } as CSSProperties}
			/>
		</div>
	);
};

/**
 * Render repeated words for the moving strip without storing DOM-only state.
 */
const Marquee = ({ totalAnimalCount }: { totalAnimalCount: number }) => {
	const marqueeWords = [
		"Free forever",
		"No signup required",
		`${totalAnimalCount} animals`,
		"Pick your favorite",
		"For any profile",
	];
	const items = marqueeWords.map((word) => (
		<span key={word}>
			{word}
			<span className="star">✦</span>
		</span>
	));

	return (
		<div className="marquee" aria-hidden="true">
			<div className="marquee-track">
				<div className="marquee-group">{items}</div>
				<div className="marquee-group">{items}</div>
			</div>
		</div>
	);
};

/**
 * Render AvatarAnimals landing page and random spotlight.
 */
export function App() {
	const [animals, setAnimals] = useState<AnimalEntry[]>(fallbackAnimals);
	const [currentAnimal, setCurrentAnimal] = useState<AnimalEntry | null>(() => {
		const requestedSlug = new URLSearchParams(window.location.search).get(
			"animal",
		);

		return (
			findAnimalBySlug(fallbackAnimals, requestedSlug) ??
			fallbackSpotlightAnimal
		);
	});
	const [toastMessage, setToastMessage] = useState<string | null>(null);

	/**
	 * Copy the current animal URL and show short-lived feedback.
	 */
	const copyCurrentAnimalUrl = async () => {
		if (!currentAnimal) {
			return;
		}

		try {
			await navigator.clipboard.writeText(currentAnimal.imageUrl);
			setToastMessage("URL copied ✓");
		} catch {
			setToastMessage("Copy failed");
		}
	};

	/**
	 * Scroll the featured animal section into view.
	 */
	const scrollToSpotlight = () => {
		window.requestAnimationFrame(() => {
			document
				.getElementById("spotlight")
				?.scrollIntoView({ behavior: "smooth", block: "center" });
		});
	};

	/**
	 * Pick a fresh animal and move attention to the spotlight, matching the HTML prototype.
	 */
	const rollRandomAnimal = () => {
		const nextAnimal = getRandomAnimal(animals);
		if (!nextAnimal) {
			return;
		}

		setCurrentAnimal(nextAnimal);
		scrollToSpotlight();
	};

	/**
	 * Feature a chosen animal from the roster strip.
	 */
	const focusSpotlightAnimal = (animal: AnimalEntry) => {
		setCurrentAnimal(animal);
		scrollToSpotlight();
	};

	useEffect(() => {
		let isMounted = true;
		const requestedSlug = new URLSearchParams(window.location.search).get(
			"animal",
		);

		fetchAnimals()
			.then((fetchedAnimals) => {
				if (!isMounted) {
					return;
				}

				setAnimals(fetchedAnimals);
				setCurrentAnimal(
					(animal) =>
						findAnimalBySlug(fetchedAnimals, requestedSlug) ??
						findAnimalBySlug(fetchedAnimals, animal?.animal ?? null) ??
						findAnimalBySlug(fetchedAnimals, "hedgehog") ??
						fetchedAnimals[0] ??
						animal,
				);
			})
			.catch(() => {
				if (isMounted) {
					setToastMessage("Showing bundled animals");
				}
			});

		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		if (window.location.hash !== "#spotlight") {
			return;
		}

		window.requestAnimationFrame(() => {
			document
				.getElementById("spotlight")
				?.scrollIntoView({ behavior: "smooth", block: "center" });
		});
	}, []);

	useEffect(() => {
		if (!toastMessage) {
			return;
		}

		const timeoutId = window.setTimeout(() => {
			setToastMessage(null);
		}, copyConfirmationDuration);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [toastMessage]);

	const spotlightAnimalName = currentAnimal
		? formatAnimalName(currentAnimal.animal)
		: "hedgehog";
	const rosterAnimals = animals.length > 0 ? animals : fallbackAnimals;
	const totalAnimalCount = rosterAnimals.length;
	const featureCards = [
		{
			number: "01 · FREE",
			title: (
				<>
					Free to use,
					<br />
					forever.
				</>
			),
			body: "Download what you like, use it wherever you like. No watermark, no account, no catch.",
			animal: "capybara",
		},
		{
			number: "02 · USE ANYWHERE",
			title: (
				<>
					Profiles, chats,
					<br />
					side projects.
				</>
			),
			body: "Discord, Slack, GitHub, your group chat — pick an animal and drop it in. Works anywhere you need a fun avatar.",
			animal: "fox",
		},
		{
			number: "03 · GROWING",
			title: (
				<>
					{totalAnimalCount} species
					<br />
					and counting.
				</>
			),
			body: "New animals added regularly. Got a favorite we're missing? Let us know and it might be next.",
			animal: "opossum",
		},
	];

	return (
		<>
			<SiteChrome activePage="home" />
			<main className="page active">
				<section className="hero">
					<div className="float-layer" aria-hidden="true">
						{heroFloaters.map((floater) => {
							const animal =
								findAnimalBySlug(animals, floater.animal) ??
								findAnimalBySlug(fallbackAnimals, floater.animal);
							if (!animal) {
								return null;
							}

							return (
								<button
									key={floater.animal}
									type="button"
									className={`float-animal ${floater.className}`}
									onClick={() => setCurrentAnimal(animal)}
									tabIndex={-1}
								>
									<img
										src={animal.imageUrl}
										alt={`${formatAnimalLabel(animal.animal)} avatar`}
									/>
								</button>
							);
						})}
					</div>

					<h1 className="hero-title">
						<span className="l1">A little</span>
						<span className="l2">
							<span className="l2a">zoo for</span>
							<span className="l2b">your</span>
						</span>
						<span className="l3">
							<span className="l3a">profile</span>
							<span className="l3b">picture.</span>
						</span>
					</h1>

					<p className="hero-sub">
						<strong>{totalAnimalCount} animal avatars</strong> for your profile,
						your group chat, your site. Free to use, no sign-up, no strings.
					</p>

					<div className="hero-actions">
						<a className="btn primary" href="/animals">
							Browse the animals <span className="arrow">→</span>
						</a>
						<button type="button" className="btn" onClick={rollRandomAnimal}>
							Pick one for me <span className="arrow">↻</span>
						</button>
					</div>
				</section>

				<Marquee totalAnimalCount={totalAnimalCount} />

				<section className="cards">
					<div className="card-grid">
						{featureCards.map((card) => {
							const animal = findAnimalBySlug(fallbackAnimals, card.animal);

							return (
								<article className="card" key={card.number}>
									<div className="card-copy">
										<div className="num">{card.number}</div>
										<h3>{card.title}</h3>
										<p>{card.body}</p>
									</div>
									{animal ? (
										<div className="card-illo-slot" aria-hidden="true">
											<div className="illo">
												<img src={animal.imageUrl} alt="" />
											</div>
										</div>
									) : null}
								</article>
							);
						})}
					</div>
				</section>

				<section className="spotlight" id="spotlight">
					<div className="spot-inner">
						<div>
							<div className="spot-label">
								<span className="pulse" /> Featured animal
							</div>
							<h2 className="spot-name">
								the <em>{spotlightAnimalName}</em>
							</h2>
							<div className="spot-actions">
								<button
									type="button"
									className="btn spot-roll-btn"
									onClick={rollRandomAnimal}
								>
									Show me another ↻
								</button>
								{currentAnimal ? (
									<a
										className="btn"
										href={currentAnimal.imageUrl}
										download={`${currentAnimal.animal}.webp`}
									>
										Download <span className="arrow">↓</span>
									</a>
								) : null}
								<button
									type="button"
									className="btn"
									onClick={copyCurrentAnimalUrl}
								>
									Copy URL <span className="arrow">⧉</span>
								</button>
								<div className={`toast ${toastMessage ? "show" : ""}`}>
									{toastMessage ?? "Link copied"}
								</div>
							</div>
						</div>
						<div className="spot-img-wrap">
							{currentAnimal ? (
								<SpotlightAnimalImage
									animal={currentAnimal}
									alt={`${formatAnimalLabel(currentAnimal.animal)} avatar`}
								/>
							) : null}
						</div>
					</div>
				</section>

				<section className="roster">
					<h3>The full roster</h3>
					<div className="sub">
						all {totalAnimalCount} animals, in one long line
					</div>
					<RosterStrip
						key={rosterAnimals.map((animal) => animal.animal).join("|")}
						animals={rosterAnimals}
						onSelectAnimal={focusSpotlightAnimal}
					/>
				</section>

				<EndpointsBlock />

				<CreatorBanner />

				<footer>
					<h4>
						Find <em>your</em>
						<br />
						animal.
					</h4>
					<div className="foot-row">
						<div>© {new Date().getFullYear()} AVATARANIMALS.COM</div>
						<div>{totalAnimalCount} SPECIES · FREE TO USE</div>
						<div>MADE WITH AI</div>
					</div>
				</footer>
			</main>
		</>
	);
}

export default App;
